import IconCreateAccount from '@/assets/dashboard/create-account.svg'
import IconCreateChannel from '@/assets/dashboard/create-channel.svg'
import IconCreatePackage from '@/assets/dashboard/create-package.svg'
import IconCreatePlaylist from '@/assets/dashboard/create-playlist.svg'
import IconCreateWidget from '@/assets/dashboard/create-widget.svg'
import IconGetHelp from '@/assets/dashboard/get-help.svg'
import IconImportStream from '@/assets/dashboard/import-stream.svg'
import IconReviewKnowledge from '@/assets/dashboard/review-knowledge.svg'
import IconReviewReports from '@/assets/dashboard/review-reports.svg'
import IconUploadVideo from '@/assets/dashboard/upload-video.svg'
import { KeyMap } from '@/constants'
import languageCodes from '@/data/languages'
import i18n from '@/i18n'
import { ContentPath, CustomAxiosError, DateRangeNames, LoadingState, ReportColumns, VideoStatus, WelcomeCard, VideoTag, Orientation, CountryRestrictionType } from '@/interfaces'
import router from '@/router'
import { apiService } from '@/services'
import { HTTP_STATUS_OK, PASTEL_COLORS, TRUVID_GENERAL_FOLDER, VIDEO_EXTENSIONS, NOT_AVAILABLE, TRUVID_TIME_ZONES, TOOLTIP_PLACEMENT } from '@/services/constants'
import { VideoSettingsDefaults } from '@/services/constants'
import { ReportTable, MenuReportColumn } from '@/services/report/types'
import store from '@/store'
import { DateTimeFormatOptions } from '@intlify/core-base'
import { useDebounceFn, useThrottleFn } from '@vueuse/core'
import { add, endOfDay, endOfYesterday, format, setDate, startOfDay, startOfMonth, startOfYesterday } from 'date-fns'
import { UAParser } from 'ua-parser-js'
import { uid } from 'uid'
import { useI18n } from 'vue-i18n'
import { RouteLocationNormalizedLoaded } from 'vue-router'
import { UserRolesNames } from './organization-user/constants'
import type { Folder } from '@/services/folder/types'
import type { Playlist, PlaylistInput } from '@/services/playlist/types'
import type { UploadVideo, Video } from '@/services/videos/types'
import { compare } from 'compare-versions'
import countryCode from 'i18n-iso-countries'
import enLocale from 'i18n-iso-countries/langs/en.json'
import CustomConfirmation, { ModalProps } from '@/components/modals/custom-modal/modal-action-components/CustomConfirmation.vue'
import { markRaw } from 'vue'
import { ResizeHandleTypeRoutes } from '@/composables/viewPortResizeHandleType'
import { ModalType, initAndRevealMainModal, initAndRevealOverlayModal } from '@/components/modals/modals-util'
import { useViewPort } from '@/composables'
import { Placement } from '@floating-ui/vue'
import { Country } from './general/types'
import { IModalProps } from '@/store/custom-modal.module'
import { checkDomainPermission } from '@/store/permissions'
import { VideoDraft } from '@/services/video-drafts/types'

countryCode.registerLocale(enLocale)

export async function loadGlobalReferenceData(): Promise<void> {
  const referenceDataCalls: Promise<unknown>[] = [
    apiService.general.getCountries(),
    apiService.general.getCategories(),
    apiService.general.getInterests(),
    apiService.general.getLanguages()
  ]

  const referenceDataNames = ['countries', 'categories', 'interests', 'languages']

  if (checkDomainPermission()) {
    referenceDataCalls.push(
      apiService.domain.getDomains({
        page_number: 1,
        results_per_page: 10,
        sort_by: 'domain',
        sort_order: 'asc',
        list_only: true
      })
    )
    referenceDataNames.push('domains')
  }

  const referenceDataResults = await Promise.allSettled(referenceDataCalls)

  referenceDataResults.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.error(`Failed to load global reference data (${referenceDataNames[index]}):`, result.reason)
    }
  })
}

export function transfromReportsColumns(columns: ReportColumns): MenuReportColumn[] {
  const data: MenuReportColumn[] = []
  if (columns && columns.columns) {
    columns.columns.forEach((column) => {
      const columnData = {
        headerName: 'reports-column-' + column.column_name,
        field: column.column_name,
        sortable: column.orderable,
        isKey: column.is_default,
        is_selected: column.is_default,
        is_default: column.is_default,
        searchable: column.searchable,
        is_dimension: column.is_dimension
      }
      data.push(columnData)
    })
  }
  return data
}

export function transfromReportsRows(table: ReportTable) {
  if (!table || !table.data) return []

  const { columns, data } = table
  return data.map((element) => {
    return Object.fromEntries(columns.map((col, i) => [col.column_name, element[i]]))
  })
}

//amount2 - i.e server calls
//amount1 - i.e revenue
export function transformDashboardGraphData(data, type: string[], reverse = false) {
  const graphData = []
  if (data !== null) {
    Object.keys(data[type[0]]).forEach((key) => {
      let date = key.includes(':') ? key.replace(' ', 'T') : key + 'T00:00:00'
      const localOffset = new Date().getTimezoneOffset()
      date = date + (localOffset > 0 ? '-' : '+') + String(Math.floor(Math.abs(localOffset) / 60)).padStart(2, '0') + ':' + String(Math.abs(localOffset) % 60).padStart(2, '0')
      graphData.push({
        date: new Date(date),
        amount1: parseFloat(!reverse ? data[type[0]][key].toFixed(2) : data[type[1]][key].toFixed(2)),
        amount2: parseFloat(!reverse ? data[type[1]][key].toFixed(2) : data[type[0]][key].toFixed(2))
      })
    })
  }
  return graphData.length > 0 ? graphData : null
}

export function getVideoPositionString(position: number) {
  let time: string
  if (position === 0) {
    return '00:00:00.00'
  }
  if (position > 60 * 60) {
    time = Math.floor(position / 3600)
      .toString()
      .padStart(2, '0')
    position = position - Math.floor(position / 3600) * 3600
  } else {
    time = '00'
  }

  if (position > 60) {
    time +=
      ':' +
      Math.floor(position / 60)
        .toString()
        .padStart(2, '0')
    position = position - Math.floor(position / 60) * 60
  } else {
    time += ':00'
  }
  const seconds = Math.round(position * 100)
    .toString()
    .padStart(4, '0')

  time += ':' + seconds.substring(0, 2) + '.' + seconds.substring(2)

  return time
}

export function lerpColor(pFrom: number, pTo: number, pRatio: number) {
  const ar = (pFrom & 0xff0000) >> 16
  const ag = (pFrom & 0x00ff00) >> 8
  const ab = pFrom & 0x0000ff
  const br = (pTo & 0xff0000) >> 16
  const bg = (pTo & 0x00ff00) >> 8
  const bb = pTo & 0x0000ff
  const rr = ar + pRatio * (br - ar)
  const rg = ag + pRatio * (bg - ag)
  const rb = ab + pRatio * (bb - ab)

  return `#${((rr << 16) + (rg << 8) + (rb | 0)).toString(16).padStart(6, '0').slice(-6)}`
}

export function numberCompact(num: number): string {
  if (typeof num === 'number') {
    const formatter = Intl.NumberFormat('en', { notation: Math.abs(num) / 1000 > 1 ? 'compact' : 'standard' })
    return formatter.format(Number(num.toFixed(2)))
  }
  return '0'
}

export function commaSeparateNumber(val) {
  // remove sign if negative
  let sign = 1
  if (val < 0) {
    sign = -1
    val = -val
  }

  if (isNaN(parseFloat(val))) {
    return ''
  }

  // trim the number decimal point if it exists
  let num = val.toString().includes('.') ? val.toString().split('.')[0] : val.toString()

  while (/(\d+)(\d{3})/.test(num.toString())) {
    // insert comma to 4th last position to the match number
    num = num.toString().replace(/(\d+)(\d{3})/, '$1' + ',' + '$2')
  }

  // add number after decimal point
  if (val.toString().includes('.')) {
    num = num + '.' + val.toString().split('.')[1].slice(0, 2)
  }
  // return result with - sign if negative
  return sign < 0 ? '-' + num : num
}

export function debounce(fn, wait) {
  return useDebounceFn(fn, wait)
}

export function throttle(value, delay?: number) {
  return useThrottleFn(value, delay)
}

export function getDateTimeByLocale(date: string): string {
  return Intl.DateTimeFormat(navigator.language).format(new Date(date)).toString()
}

// Helper function to extract date parts for the target timezone
function getDatePartsForTimeZone(utcTimestamp: number, timeZone: string) {
  const options = {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false
  }
  const dateParts = new Intl.DateTimeFormat('en-US', options).formatToParts(new Date(utcTimestamp))
  const year = parseInt(dateParts.find((p) => p.type === 'year').value)
  const month = parseInt(dateParts.find((p) => p.type === 'month').value) - 1 // Month is 0-indexed
  const day = parseInt(dateParts.find((p) => p.type === 'day').value)
  const hour = parseInt(dateParts.find((p) => p.type === 'hour').value)
  const minute = parseInt(dateParts.find((p) => p.type === 'minute').value)
  return { year, month, day, hour, minute }
}

// Function to create a Date object for a given dateTime string, time zone
export function createDateForTimeZone(dateTime: string, timeZone: string): Date {
  if (dateTime.length === 10) {
    dateTime += 'T00:00'
  }
  const localDate = new Date(dateTime)
  const utcTimestamp = localDate.getTime() + localDate.getTimezoneOffset() * 60000
  const { year, month, day, hour, minute } = getDatePartsForTimeZone(utcTimestamp, timeZone)
  return new Date(Date.UTC(year, month, day, hour, minute))
}

// Updated function to convert an existing Date object to a different time zone with option for start or end of the day
export function convertDateForTimeZone(localDate: Date, timeZone: string, isEndOfDay = false): Date {
  const utcTimestamp = localDate.getTime() + localDate.getTimezoneOffset() * 60000
  const { year, month, day, hour, minute } = getDatePartsForTimeZone(utcTimestamp, timeZone)
  return new Date(year, month, day, isEndOfDay ? 23 : hour, isEndOfDay ? 59 : minute)
}

export function formatDateDateMonthYear(isoDate: string): string {
  const date = new Date(isoDate)
  if (isNaN(date.getTime())) return ''
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' }
  return new Intl.DateTimeFormat('en-US', options).format(date)
}

export function getUTCDateString(date: string): string {
  const d = new Date(date)
  const year = d.getUTCFullYear().toString().length > 2 ? d.getUTCFullYear().toString().slice(2, 4) : d.getUTCFullYear().toString().padStart(2, '0')
  const month = (d.getMonth() + 1).toString().padStart(2, '0')
  const day = d.getDate().toString().padStart(2, '0')
  return `${month}/${day}/${year}`
}

export function getDateString(date: string): string {
  const d = new Date(date)
  return getDateStringByDate(d)
}

export function getDateStringReports(date: string, groupBy: string): string {
  const [datePart, timePart] = date.split(' ')
  const [year, month, day] = datePart.split('-')
  const shortYear = year.slice(-2)
  const time = timePart ? timePart.slice(0, 5) : ''

  switch (groupBy) {
    case 'month':
      return `${month}/${shortYear}`
    case 'day':
      return day ? `${month}/${day}/${shortYear}` : `${month}/${year}`
    case 'hour':
      return day ? `${month}/${day}/${shortYear} ${time}` : `${month}/${year}`
    default:
      return ''
  }
}

export function getDateStringByDate(d: Date): string {
  const year = d.getFullYear().toString().length > 2 ? d.getFullYear().toString().slice(2, 4) : d.getFullYear().toString().padStart(2, '0')
  const month = (d.getMonth() + 1).toString().padStart(2, '0')
  const day = d.getDate().toString().padStart(2, '0')
  return `${month}/${day}/${year}`
}

export function getDateStringForGraphBubble(d: Date, rangeDates: string[]): string {
  const year = d.getFullYear().toString()
  const month = capitalize(i18n.global.t(monthKeys[d.getMonth()]))
  const day = d.getDate().toString().padStart(2, '0')
  const hours = d.getHours().toString().padStart(2, '0') + ':00'
  const range = (new Date(rangeDates[1]).getTime() - new Date(rangeDates[0]).getTime()) / 1000
  if (range <= 86400) {
    return `${month} ${day} ${year} ${hours}`
  } else if (range <= 86400 * 60) {
    return `${month} ${day} ${year}`
  } else {
    return `${month} ${year}`
  }
}

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}
const monthKeys = [
  'datepicker-jan',
  'datepicker-feb',
  'datepicker-mar',
  'datepicker-apr',
  'datepicker-may',
  'datepicker-jun',
  'datepicker-jul',
  'datepicker-aug',
  'datepicker-sep',
  'datepicker-oct',
  'datepicker-nov',
  'datepicker-dec'
]

export function getDateFormat(date: string) {
  const d = new Date(date)
  return formatDateWithMonthName(d)
}

export function formatDateWithMonthName(date: Date): string {
  const { t } = useI18n()
  if (isNaN(date.getTime())) return '' // handle invalid date
  const day: number = date.getDate()
  const month = capitalize(t(monthKeys[date.getMonth()]))
  const year: number = date.getFullYear()
  return `${month} ${day}, ${year}`
}

export function formatDate(date) {
  const options: DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }

  if (date) {
    return new Date(date).toLocaleDateString(undefined, options)
  }
}

type TimeThreshold = {
  limit: number
  suffix: string
  divisor: number
}

export function isToday(dateStr: string): boolean {
  const d = new Date(dateStr)
  const today = new Date()
  return d.toDateString() === today.toDateString()
}

export function formatTimeAgoShort(dateString: string): string {
  const inputDate = new Date(dateString)
  if (isNaN(inputDate.getTime())) return ''

  const now = new Date()

  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const inputDateOnly = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate())

  const diffMs = nowDate.getTime() - inputDateOnly.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  const thresholds: TimeThreshold[] = [
    { limit: 365, suffix: 'Y', divisor: 365 },
    { limit: 30, suffix: 'M', divisor: 30 },
    { limit: 7, suffix: 'W', divisor: 7 }
  ]

  for (const { limit, suffix, divisor } of thresholds) {
    if (diffDays >= limit) {
      return `${Math.floor(diffDays / divisor)}${suffix}`
    }
  }

  return `${diffDays}D`
}

export function isDevelopment() {
  return process.env.NODE_ENV === 'development'
}

export function isProduction(): boolean {
  return import.meta.env.VITE_APP_IS_PRODUCTION === 'true'
}

export function isDevConsole() {
  return window.location.origin.includes('console-dev')
}

export function isValidDateString(date: string): boolean {
  return !!(date && date !== '' && date !== '0001-01-01T00:00:00Z')
}

export function getDisplayLanguage(langCode): string {
  const lang = languageCodes[langCode.slice(0, 2)]
  return lang ? lang.name : langCode
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return email && emailRegex.test(email) && email.length <= 120
}

export function shortenText(text: string, maxLength = 30): string {
  if (text && text.length > maxLength) {
    return text.slice(0, maxLength) + '...'
  }
  return text
}

export function checkName(first: string, second: string): boolean {
  return first.toLowerCase() === second.toLowerCase()
}

export function editDistance(s, t) {
  if (!s.length) return t.length
  if (!t.length) return s.length
  const arr = []
  for (let i = 0; i <= t.length; i++) {
    arr[i] = [i]
    for (let j = 1; j <= s.length; j++) {
      arr[i][j] = i === 0 ? j : Math.min(arr[i - 1][j] + 1, arr[i][j - 1] + 1, arr[i - 1][j - 1] + (s[j - 1] === t[i - 1] ? 0 : 1))
    }
  }
  return arr[t.length][s.length]
}

export function getRouteHash() {
  return router.currentRoute.value.path
    .split('/')
    .filter((_, index) => index < Object.keys(ContentPath).length / 2) //we ignore extra elements in the path
    .join('')
}

export function getRandomColor(onlyBright = false) {
  const red = Math.floor(Math.random() * 256 + (onlyBright ? 128 : 0))
  const green = Math.floor(Math.random() * 256 + (onlyBright ? 128 : 0))
  const blue = Math.floor(Math.random() * 256 + (onlyBright ? 128 : 0))
  const color = `rgb(${red}, ${green}, ${blue})`
  store.commit('user/setUserColor', color)
  return color
}

export function getRandomPastelColor(): string {
  const randomIndex = Math.floor(Math.random() * PASTEL_COLORS.length)
  const color = PASTEL_COLORS[randomIndex].rgb
  store.commit('user/setUserColor', color)
  return color
}

export function formatText(text: string, max: number) {
  if (text.length <= max) {
    return text
  } else {
    const firstRow = text.substring(0, max)
    return `${firstRow}..`
  }
}

export function formatText2Lines(text: string, max: number) {
  if (text.length <= max) {
    return text
  } else {
    const firstRow = text.substring(0, max)
    const secondRow = text.substring(max, max + max)
    return `${firstRow}\n${secondRow}..`
  }
}

export function removeEmptyItems(body: unknown, excludedArray: string[] = []) {
  for (const [key, value] of Object.entries(body)) {
    if (
      !excludedArray.includes(key) && // do not remove item if it is in the excluded list
      ((Array.isArray(value) && (value === undefined || value.length == 0)) || value === null || value === 'undefined' || value === '')
    ) {
      delete body[key]
    }
  }
}

// This method will return json contains only the key/values that have been changed between new and old jsons

export function buildRequestBody(origObj, newObj, primaryIdKey = undefined) {
  /* eslint-disable */
  const diff = {}

  for (const key in origObj) {
    if (origObj.hasOwnProperty(key)) {
      if (!newObj.hasOwnProperty(key)) {
      } else if (typeof origObj[key] === 'object' && typeof newObj[key] === 'object') {
        const nestedDiff = buildRequestBody(origObj[key], newObj[key], primaryIdKey)
        if (Object.keys(nestedDiff).length > 0) {
          diff[key] = newObj[key]
        }
      } else if (origObj[key] !== newObj[key]) {
        diff[key] = newObj[key]
      }
    }
  }

  // Iterate over the keys in obj2 to find missing keys
  for (const key in newObj) {
    if (newObj.hasOwnProperty(key) && !origObj.hasOwnProperty(key)) {
      diff[key] = newObj[key]
    }
  }

  if (primaryIdKey) {
    diff[primaryIdKey] = newObj[primaryIdKey]
  }
  return diff
  /* eslint-enable */
}

export function validateURL(url, urls) {
  const pattern =
    /^(?:(?:(?:https):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i

  if (pattern.test(url)) {
    return !(urls.value != undefined && urls.value.some((u) => u.url === url))
  }

  return false
}

export function isValidURL(url: string): boolean {
  //eslint-disable-next-line
  const pattern = /(^ftp|^http|^https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/
  if (pattern.test(url)) {
    return true
  } else {
    return false
  }
}

export function cleanUpRoles(roles, inverse = false) {
  const newPermissions = JSON.parse(JSON.stringify(roles))
  const owners = newPermissions.filter((r) => {
    return r.name.includes('owner') && r.selected
  })
  owners.forEach((owner) => {
    const type = owner.name.substring(0, owner.name.indexOf('_'))
    newPermissions.forEach((role) => {
      if (!role.name.includes('owner') && role.name.includes(type)) {
        role.selected = inverse
      }
    })
  })
  return newPermissions
}

export function convertRolesToNumbers(roles): number[] {
  let newPermissions = roles.filter((r) => {
    return r.selected
  })
  newPermissions = newPermissions.map((r) => {
    return r.value
  })
  return newPermissions
}

export function isAgencyOwner(): boolean {
  return store.getters['user/isRole'](UserRolesNames.AgencyOwner)
}

export function isContentAccounting(): boolean {
  return store.getters['user/isRole']('content_accounting')
}

export function isPublisherAccounting(): boolean {
  return store.getters['user/isRole'](UserRolesNames.PublisherAccounting)
}

export function isPublisher(): boolean {
  return store.getters['user/isRole'](UserRolesNames.PublisherOwner)
}

export function isPublisherEditor(): boolean {
  return store.getters['user/isRole'](UserRolesNames.PublisherEditor)
}

export function isContentOwner(): boolean {
  return store.getters['user/isRole'](UserRolesNames.ContentOwner)
}

export function isContentEditor(): boolean {
  return store.getters['user/isRole'](UserRolesNames.ContentEditor)
}

export function isRole(role): boolean {
  return store.getters['user/isRole'](role)
}

export function hasRoles(roles): boolean {
  if (!Array.isArray(roles)) {
    return false
  }
  return roles.every((role) => isRole(role))
}

export function containsRole(role: string): boolean {
  return !!store.state.user.roles.find((userRole) => {
    return userRole.name === role
  })
}

export function isOwnerAccount(): boolean {
  return (
    containsRole(UserRolesNames.OrganizationAdmin) ||
    containsRole(UserRolesNames.AgencyOwner) ||
    containsRole(UserRolesNames.PublisherOwner) ||
    containsRole(UserRolesNames.ContentOwner)
  )
}

export function isOrganizationAdmin(): boolean {
  return containsRole(UserRolesNames.OrganizationAdmin)
}

export function hasOnlyContentEditingRoles(): boolean {
  // Get the user roles from the store
  const userRoles = store.state.user.roles
  // Extract role names from userRoles
  const userRoleNames = userRoles.map((role) => role.name)
  const hasContentOwner = userRoleNames.includes(UserRolesNames.ContentOwner)
  const hasContentEditor = userRoleNames.includes(UserRolesNames.ContentEditor)
  const hasPublisherOwner = userRoleNames.includes(UserRolesNames.PublisherOwner)
  const hasPublisherEditor = userRoleNames.includes(UserRolesNames.PublisherEditor)

  return (hasContentOwner || hasContentEditor) && !hasPublisherOwner && !hasPublisherEditor
}

export function sortFolderArrayByaAzZ(arrayToSort: Folder[]) {
  return arrayToSort.sort(function (a, b) {
    const nameA = a.name.toUpperCase() // ignore upper and lowercase
    const nameB = b.name.toUpperCase() // ignore upper and lowercase
    if (nameA < nameB) {
      return -1
    }
    if (nameA > nameB) {
      return 1
    }
    // names must be equal
    return 0
  })
}

export function formatDuration(durationInMs): string {
  const durationInSeconds = Math.floor(durationInMs / 1000)
  const hours = Math.floor(durationInSeconds / 3600)
  const minutes = Math.floor((durationInSeconds % 3600) / 60)
  const seconds = durationInSeconds % 60
  const formattedMinutes = minutes.toString().padStart(2, '0')
  const formattedSeconds = seconds.toString().padStart(2, '0')
  if (hours > 0) {
    const formattedHours = hours.toString().padStart(2, '0')
    return formattedHours + ':' + formattedMinutes + ':' + formattedSeconds
  }
  return formattedMinutes + ':' + formattedSeconds
}

export function getDatesRangeStringForApi(date: Date) {
  return format(date, "yyyy-MM-dd'T'HH:mm")
}

// Helper function to get a cookie by name
export function getCookie(name) {
  const cookies = document.cookie.split(';')
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim()
    // Check if the cookie name matches
    if (cookie.startsWith(name + '=')) {
      return cookie.substring(name.length + 1)
    }
  }
  return null
}

// Helper function to set a cookie. Pass `days` for a persistent cookie; omit for a session cookie.
export function setCookie(name, value, days?: number) {
  let expires = ''
  if (days !== undefined) {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    expires = `; expires=${date.toUTCString()}`
  }
  document.cookie = `${name}=${value}; path=/${expires}`
}

export function deleteCookie(name) {
  document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
}

export function getPlayerCDNDomain(): string {
  return import.meta.env.VITE_APP_PLAYER_TAG_CDN_DOMAIN
}

function getIsMaintenanceInProgress(): boolean {
  const val = import.meta.env.VITE_APP_IS_MAINTENANCE_IN_PROGRESS
  return val === 'true'
}

function getIsMaintenanceScheduled(): boolean {
  const val = import.meta.env.VITE_APP_IS_MAINTENANCE_SCHEDULED
  return val === 'true'
}

export const isMaintenanceInProgress = getIsMaintenanceInProgress()
export const isMaintenanceScheduled = getIsMaintenanceScheduled()

export function timeSince(lastLoginDate: string | Date) {
  const now = new Date()
  const lastLogin = new Date(lastLoginDate)
  const timeDifference = now.getTime() - lastLogin.getTime()
  if (timeDifference < 0) return i18n.global.t('login-just-now')

  const minutes = Math.floor(timeDifference / (1000 * 60))
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const months = Math.floor(days / 30)
  const years = Math.floor(months / 12)

  let period: string

  if (years > 0) {
    const ago = years > 1 ? i18n.global.t('years-ago') : i18n.global.t('year-ago')
    period = `${years} ${ago}`
  } else if (months > 0) {
    const ago = months > 1 ? i18n.global.t('months-ago') : i18n.global.t('month-ago')
    period = `${months} ${ago}`
  } else if (days > 0) {
    const ago = days > 1 ? i18n.global.t('days-ago') : i18n.global.t('day-ago')
    period = `${days} ${ago}`
  } else if (hours > 0) {
    const ago = hours > 1 ? i18n.global.t('hours-ago') : i18n.global.t('hour-ago')
    period = `${hours} ${ago}`
  } else if (minutes > 0) {
    const ago = minutes > 1 ? i18n.global.t('minutes-ago') : i18n.global.t('minute-ago')
    period = `${minutes} ${ago}`
  } else {
    period = i18n.global.t('login-just-now')
  }

  return period
}

export async function checkReportsStatus() {
  let message = ''
  let onState = null

  try {
    const data = await apiService.report.checkReportStatus()

    if (data.error_message !== null && data.error_message !== '') {
      message = data.error_message
      onState = true
    }
  } catch (error) {
    // Handle errors from apiService.report.checkReportStatus() if needed
    console.error(error)
  }

  return { onState: onState, message: message }
}

export function getDateRanges(range: DateRangeNames) {
  const today = endOfDay(new Date())
  let outDates = []
  switch (range) {
    case DateRangeNames.Today:
      outDates = [startOfDay(new Date()), endOfDay(new Date())]
      break
    case DateRangeNames.Yesterday:
      outDates = [startOfYesterday(), endOfYesterday()]
      break
    case DateRangeNames.Last7days:
      outDates = [startOfDay(add(new Date(), { days: -7 })), endOfYesterday()]
      break
    case DateRangeNames.Last30days:
      outDates = [startOfDay(add(new Date(), { days: -30 })), endOfYesterday()]
      break
    case DateRangeNames.ThisMonth:
      outDates = [startOfMonth(new Date()), endOfDay(new Date())]
      break
    case DateRangeNames.LastMonth:
      outDates = [startOfDay(startOfMonth(setDate(today, 0))), setDate(today, 0)]
      break
    default:
      outDates = [new Date(today.getFullYear() - 1, 2, 9), new Date(today.getFullYear() - 1, 2, 30)]
      break
  }
  return [
    ...outDates.slice(0, 2).map((d) => {
      return format(d, "yyyy-MM-dd'T'HH:mm")
    }),
    range
  ]
}

export function initSetupDate(timeRange: DateRangeNames) {
  const endDate = new Date()
  const startDate = new Date(endDate)
  const formatDateTime = (date: Date) => {
    const pad = (num: number) => String(num).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
  }

  switch (timeRange) {
    case DateRangeNames.Yesterday:
      startDate.setDate(endDate.getDate() - 1)
      break

    case DateRangeNames.Last7days:
      startDate.setDate(endDate.getDate() - 7)
      break

    case DateRangeNames.Last30days:
      startDate.setDate(endDate.getDate() - 30)
      break

    case DateRangeNames.ThisMonth:
      startDate.setDate(1)
      break

    case DateRangeNames.LastMonth:
      startDate.setMonth(endDate.getMonth() - 1, 1)
      endDate.setMonth(endDate.getMonth() - 1)
      endDate.setDate(new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate())
      break

    default:
      startDate.setFullYear(startDate.getFullYear(), 0, 1)
      break
  }

  startDate.setHours(0, 0, 0, 0)
  endDate.setHours(23, 59, 59, 999)

  return [formatDateTime(startDate), formatDateTime(endDate), timeRange]
}

export function filterArrayByIds(array, keys) {
  return array.filter((item) => keys.includes(item.id))
}

export function updateTitleLanguage(metaTitle) {
  if (metaTitle) {
    const title = i18n.global.t('document-title-' + metaTitle)
    document.title = title ? 'Truvid | ' + title.charAt(0).toUpperCase() + title.slice(1) : 'Truvid'
  } else {
    document.title = 'Truvid'
  }
}

function isKeyEqual(inputKeyCode: number, targetKeyCode: number): boolean {
  return inputKeyCode === targetKeyCode
}

export function isEnter(keyCode: number): boolean {
  return isKeyEqual(keyCode, KeyMap.ENTER)
}

export function isEsc(keyCode: number): boolean {
  return isKeyEqual(keyCode, KeyMap.ESC)
}

export function sortVideosByOrder(videos_list: string[] , videos?: Video[]): Video[] | undefined {
  if (!videos || !videos_list?.length) return videos
  const orderIndex = new Map(videos_list.map((id, idx) => [id, idx]))
  return [...videos].sort((a, b) => (orderIndex.get(a.video_id) ?? Number.MAX_SAFE_INTEGER) - (orderIndex.get(b.video_id) ?? Number.MAX_SAFE_INTEGER))
}

export function pl2pl(playlist: PlaylistInput): Playlist {
  const videos_list: string[] = JSON.parse(playlist.videos_list)
  const pl: Playlist = {
    playlist_id: playlist.playlist_id,
    organization_id: playlist.organization_id,
    playlist_name: playlist.playlist_name,
    create_date: playlist.create_date,
    status: playlist.status,
    videos_list,
    videos_amount: playlist.videos_amount,
    auto_updated: playlist.auto_updated,
    is_random_order: playlist.is_random_order,
    auto_updated_max_duration: playlist.auto_updated_max_duration,
    videos: sortVideosByOrder(videos_list, playlist.videos),
    categories: playlist.categories,
    description: playlist.description,
    tags: playlist.tags
  }
  return pl
}

export function isVideoThumbnailSupported(videoURL: string): boolean {
  let fileExtension = ''
  if (videoURL && videoURL.includes('.')) {
    fileExtension = videoURL.split('.').pop()?.toLocaleLowerCase() || ''
  }

  switch (fileExtension) {
    case VIDEO_EXTENSIONS.AVI:
    case VIDEO_EXTENSIONS.MPEG:
      return false
    default:
      return true
  }
}

export function getFileNameWithoutExtension(fileName: string): string {
  if (typeof fileName !== 'string') {
    return ''
  }
  const lastDotIndex = fileName.lastIndexOf('.')
  if (lastDotIndex === -1) {
    return fileName
  }
  return fileName.substring(0, lastDotIndex)
}

export function isDefaultGeneralFolder(folderName: string): boolean {
  return folderName === TRUVID_GENERAL_FOLDER
}

export function isErroredAPIResponseStatus(res: CustomAxiosError): boolean {
  return res?.response?.status && res.response?.status !== HTTP_STATUS_OK
}

export const NewUploadVideo = (file: File): UploadVideo => ({
  file,
  id: '',
  source_url: '',
  title: getFileNameWithoutExtension(file.name),
  loadingState: LoadingState.initial,
  description: '',
  uploadProgress: 0,
  localId: uid(),
  status: VideoStatus.Request,
  thumbnail_url: '',
  pricing_model: VideoSettingsDefaults.pricing_model,
  exposure: 'public',
  private: VideoSettingsDefaults.private,
  guid: VideoSettingsDefaults.guid,
  clickthrough_urls: [],
  language_id: VideoSettingsDefaults.language_id,
  folder: VideoSettingsDefaults.folder,
  categories: VideoSettingsDefaults.categories,
  tags: VideoSettingsDefaults.tags ?? [],
  controller: undefined,
  thumbnailsLoading: true,
  clickthroughUrls: VideoSettingsDefaults.clickthroughUrls,
  thumbnails: [],
  is_vertical: false,
  countries_allow: VideoSettingsDefaults.countries_allow,
  countries_disallow: VideoSettingsDefaults.countries_disallow,
  draftId: null,
  duration_in_ms: null
})

export const convertDraftToUploadVideo = (draft: VideoDraft): UploadVideo => {
  const filename = draft.video_title
  const file = new File([], filename, { type: 'video/mp4' })

  return {
    file,
    id: draft.id.toString(),
    source_url: draft.payload?.source_url || '',
    title: draft.payload?.title || draft.video_title,
    loadingState: LoadingState.success,
    description: draft.payload?.description || '',
    uploadProgress: 1,
    localId: uid(),
    status: VideoStatus.Uploaded,
    thumbnail_url: draft.payload?.thumbnails?.[0]?.url,
    pricing_model: draft.payload?.pricing_model ?? VideoSettingsDefaults.pricing_model,
    exposure: draft.payload?.private ? 'private' : 'public',
    private: draft.payload?.private ?? VideoSettingsDefaults.private,
    guid: draft.payload?.guid ?? VideoSettingsDefaults.guid,
    clickthroughUrls: draft.payload?.clickthrough_urls?.[0]?.url ?? VideoSettingsDefaults.clickthroughUrls,
    language_id: draft.payload?.language_id ?? VideoSettingsDefaults.language_id,
    folder: draft.payload?.folder_id ?? VideoSettingsDefaults.folder,
    categories: draft.payload?.categories ?? VideoSettingsDefaults.categories,
    tags: draft.payload?.tags ?? VideoSettingsDefaults.tags ?? [],
    controller: undefined,
    thumbnailsLoading: false,
    is_vertical: draft.orientation === Orientation.Vertical,
    thumbnails: (draft.payload?.thumbnails?.map((t) => t.url)) ?? [],
    request: {
      id: draft.id.toString(),
      signed_url: draft.payload?.source_url || '',
      public_url: draft.payload?.source_url || '',
    },
    hasCountryRestrictions: !!(draft.payload?.countries_allow?.length || draft.payload?.countries_disallow?.length),
    countryRestrictionType: draft.payload?.countries_allow?.length ? CountryRestrictionType.Include : CountryRestrictionType.Exclude,
    countries_allow: draft.payload?.countries_allow ?? VideoSettingsDefaults.countries_allow,
    countries_disallow: draft.payload?.countries_disallow ?? VideoSettingsDefaults.countries_disallow,
    isSelected: false,
    isSettingsUpdated: draft.video_settings === true,
    draftId: draft.id,
    duration_in_ms: draft.duration || null
  }
}

const AddVideo: WelcomeCard = {
  imgSrc: IconUploadVideo,
  title: 'dashboard-upload',
  subtitle: 'dashboard-new-video',
  path: '/videos/videos/add'
}
const ImportStream: WelcomeCard = {
  imgSrc: IconImportStream,
  title: 'dashboard-import-from',
  subtitle: 'dashboard-import-stream',
  path: '/videos/videos/addstream'
}
const CreatePackage: WelcomeCard = {
  imgSrc: IconCreatePackage,
  title: 'dashboard-create',
  subtitle: 'dashboard-new-package',
  path: '/packages/add'
}
const CreateWidget: WelcomeCard = {
  imgSrc: IconCreateWidget,
  title: 'dashboard-create',
  subtitle: 'dashboard-new-widget',
  path: '/widgets/create-widget-picker'
}
const CreatePlaylist: WelcomeCard = {
  imgSrc: IconCreatePlaylist,
  title: 'dashboard-create',
  subtitle: 'dashboard-new-playlist',
  path: '/library/createplaylist?new=true'
}
const ReviewReports: WelcomeCard = {
  imgSrc: IconReviewReports,
  title: 'dashboard-analyze',
  subtitle: 'dashboard-reports',
  path: '/reports'
}
const CreateChannel: WelcomeCard = {
  imgSrc: IconCreateChannel,
  title: 'content-channel-preview',
  subtitle: 'dashboard-new-channel',
  path: '/videos/channel'
}
const AddDomain: WelcomeCard = {
  imgSrc: IconCreateAccount,
  title: 'dashboard-create',
  subtitle: 'reports-column-domain',
  path: '/inventory/publisher-domains/add'
}
const CreateAccount: WelcomeCard = {
  imgSrc: IconCreateAccount,
  title: 'dashboard-create',
  subtitle: 'dashboard-new-account',
  path: '/accounts/add'
}
export const ReviewKnowledge: WelcomeCard = {
  imgSrc: IconReviewKnowledge,
  title: 'dashboard-explore',
  subtitle: 'dashboard-knowledge',
  path: '/explore-knowledge'
}
const GetHelp: WelcomeCard = {
  imgSrc: IconGetHelp,
  title: 'dashboard-get-help',
  subtitle: 'dashboard-support',
  path: '/get-help'
}

export function getDictionaryKey(): string {
  return store.state.user.roles
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((item) => item.name)
    .join('_')
}

export function getDashboardWelcomeCards() {
  const welcomeCardDictionary = {}

  welcomeCardDictionary[UserRolesNames.ContentAccounting + '_' + UserRolesNames.ContentEditor] = [AddVideo, CreatePackage, ImportStream, ReviewReports]

  welcomeCardDictionary[UserRolesNames.ContentEditor + '_' + UserRolesNames.PublisherEditor] = [AddVideo, CreatePackage, CreateWidget, CreatePlaylist]

  welcomeCardDictionary[UserRolesNames.ContentEditor + '_' + UserRolesNames.PublisherAccounting] = [AddVideo, CreatePackage, ImportStream, CreateChannel]

  welcomeCardDictionary[UserRolesNames.ContentEditor + '_' + UserRolesNames.PublisherOwner] = [AddVideo, CreatePackage, CreateWidget, CreatePlaylist]

  welcomeCardDictionary[UserRolesNames.ContentAccounting + '_' + UserRolesNames.PublisherAccounting] = [ReviewReports]

  welcomeCardDictionary[UserRolesNames.ContentAccounting + ' ' + UserRolesNames.PublisherEditor] = [ReviewReports, AddDomain, CreateWidget, CreatePlaylist]

  welcomeCardDictionary[UserRolesNames.ContentAccounting + ' ' + UserRolesNames.PublisherOwner] = [ReviewReports, AddDomain, CreateWidget, CreatePlaylist]

  welcomeCardDictionary[UserRolesNames.ContentOwner + '_' + UserRolesNames.PublisherEditor] = [AddVideo, CreatePackage, CreateWidget, CreatePlaylist]

  welcomeCardDictionary[UserRolesNames.ContentOwner + '_' + UserRolesNames.PublisherAccounting] = [AddVideo, CreatePackage, ImportStream, CreateChannel]

  welcomeCardDictionary[UserRolesNames.ContentOwner + '_' + UserRolesNames.PublisherOwner] = [AddVideo, CreatePackage, CreateWidget, CreatePlaylist]

  welcomeCardDictionary[UserRolesNames.OrganizationAdmin] = [AddVideo, CreatePackage, CreateWidget, CreatePlaylist]

  welcomeCardDictionary[UserRolesNames.PublisherAccounting + '_' + UserRolesNames.PublisherEditor] = [ReviewReports, AddDomain, CreateWidget, CreatePlaylist]

  welcomeCardDictionary[UserRolesNames.PublisherAccounting] = [ReviewReports]

  welcomeCardDictionary[UserRolesNames.ContentAccounting] = [ReviewReports]

  welcomeCardDictionary[UserRolesNames.PublisherEditor] = [ReviewReports, AddDomain, CreateWidget, CreatePlaylist]

  welcomeCardDictionary[UserRolesNames.ContentEditor] = [AddVideo, CreatePackage, ImportStream, CreateChannel]

  welcomeCardDictionary[UserRolesNames.PublisherOwner] = [ReviewReports, AddDomain, CreateWidget, CreatePlaylist]

  welcomeCardDictionary[UserRolesNames.ContentOwner] = [AddVideo, CreatePackage, ImportStream, CreateChannel]

  welcomeCardDictionary[UserRolesNames.AgencyOwner] = [ReviewReports, ReviewKnowledge, CreateAccount, GetHelp]
  return welcomeCardDictionary
}

export const keyValueIfNotNull = (key, value) => {
  if (value) {
    return { [key]: value }
  }
  return {}
}

export function removeNullOrUndefined(jsonObject) {
  if (jsonObject) {
    for (const key in jsonObject) {
      if (jsonObject[key] == null) {
        delete jsonObject[key]
      }
    }
  }
  return jsonObject
}

const parser = new UAParser()
const parserResults = parser.getResult()

export function isSafari(): boolean {
  return parserResults.browser.name === 'Safari'
}

export function broadcastChannelSupport(): boolean {
  if (parserResults.browser.name !== 'Safari') {
    return true
  } else {
    return compare(parserResults.browser.version, '15.4.0', '>=')
  }
}

export function isSafariVersionOlderThanV16(): boolean {
  const version = parserResults.browser.version
  return isSafari() && compare(version, '16', '<=')
}

export function getISOCode(countryName: string) {
  if (countryName && countryName.length > 0) {
    return countryCode.getAlpha2Code(countryName, 'en')?.toLocaleLowerCase()
  }
  return NOT_AVAILABLE
}

export function getCountryName(isoCode: string) {
  if (isoCode && isoCode.length > 0) {
    const countriesFromCache = store.getters['cache/getCountries']()
    const countryObject: Country = countriesFromCache?.countries?.find((country: Country) => country.country_code === isoCode)
    return countryObject ? countryObject.country_name : NOT_AVAILABLE
  }
  return NOT_AVAILABLE
}

export function getCountriesString(allow: string[] = [], disallow: string[] = []): { statusMessage: string; countryList: string } {
  const allowedCountries = allow?.map((countryCode) => getCountryName(countryCode))
  const disallowedCountries = disallow?.map((countryCode) => getCountryName(countryCode))

  const maxDisplayCount = 10
  const allowedCountriesToDisplay = allowedCountries?.slice(0, maxDisplayCount).join(', ')
  const disallowedCountriesToDisplay = disallowedCountries?.slice(0, maxDisplayCount).join(', ')

  const additionalAllowedMessage = allowedCountries.length > maxDisplayCount ? ` +${allowedCountries.length - maxDisplayCount} more` : ''
  const additionalDisallowedMessage = disallowedCountries.length > maxDisplayCount ? ` +${disallowedCountries.length - maxDisplayCount} more` : ''

  const statusMessage =
    allowedCountries.length > 0
      ? i18n.global.t('geo-restrictions-allow').slice(0, -8)
      : disallowedCountries.length > 0
        ? i18n.global.t('geo-restrictions-disallow').slice(0, -3)
        : ''

  let countryList = ''

  if (allowedCountries.length > 0) {
    countryList += `${i18n.global.t('geo-restrictions-allow')} ${allowedCountriesToDisplay}${additionalAllowedMessage} `
  }

  if (disallowedCountries.length > 0) {
    countryList += `${i18n.global.t('geo-restrictions-disallow')} ${disallowedCountriesToDisplay}${additionalDisallowedMessage}`
  }

  return {
    statusMessage: statusMessage,
    countryList: countryList.trim()
  }
}

export function isSubRoute(newRoute: RouteLocationNormalizedLoaded, oldRoute: RouteLocationNormalizedLoaded): boolean {
  const routes = router.getRoutes()
  const parentRoute = routes?.find((route) => {
    return route.name === oldRoute.name
  })

  if (!parentRoute) {
    return false
  }
  const newRouteIndex = parentRoute.children?.findIndex((nr) => {
    nr.name === newRoute.name
  })
  const oldRouteIndex = parentRoute.children?.findIndex((or) => {
    or.name === oldRoute.name
  })
  return newRouteIndex === oldRouteIndex
}

export function deleteCookiesWithPrefix(prefix) {
  // Get all cookies as a single string
  const cookies = document.cookie.split(';')
  // Iterate over each cookie
  for (let i = 0; i < cookies.length; i++) {
    // Get the cookie name (everything before the first '=' character)
    const cookie = cookies[i].trim()
    const cookieName = cookie.split('=')[0]
    // Check if the cookie name starts with the specified prefix
    if (cookieName.startsWith(prefix)) {
      // Delete the cookie by setting its expiry date to a past date
      document.cookie = cookieName + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'
    }
  }
}

export function getGMTOffset(timezone: string): string {
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour12: false,
      timeZoneName: 'short'
    })
    const parts = formatter.formatToParts(now)
    const offsetPart = parts.find((part) => part.type === 'timeZoneName')
    if (offsetPart) {
      const offset = offsetPart.value
      const match = offset.match(/(GMT)([+-]\d+)/i)
      if (match) {
        const hours = match[2]
        return `GMT${hours}`
      }
    }
    return offsetPart ? offsetPart.value.toUpperCase() : 'INVALID TIMEZONE'
  } catch (error) {
    return 'ERROR'
  }
}

export function isTimeZoneItem(rawTz: string): string | null {
  const matchedZone = TRUVID_TIME_ZONES.find((tz) => rawTz.startsWith(tz.name))
  return matchedZone ? matchedZone.name : null
}

export function openModalCustomConfirmation(props: ModalProps, modalType: ModalType) {
  const modalConfig = {
    componentContent: markRaw(CustomConfirmation),
    componentFooter: null,
    props,
    modalType
  } as IModalProps

  if (props.isOverlay) {
    initAndRevealOverlayModal(modalConfig)
  } else {
    initAndRevealMainModal(modalConfig)
  }
}

export function extractStoreDomainData(pattern: string, key: string): string | null {
  if (!pattern || !key) {
    return null
  }
  const domainObject = JSON.parse(pattern)
  if (key === 'id') {
    return domainObject.id
  } else if (key === 'domain') {
    return domainObject.domain
  } else {
    return null
  }
}
export function isRouteHasCachedViewType() {
  const routeName = getRouteName()
  return Object.values(ResizeHandleTypeRoutes).includes(routeName as ResizeHandleTypeRoutes)
}

export function getRouteName(): string {
  return router.currentRoute.value.name as string
}

export function getLibraryChannelHandle(): string | null {
  const path = router.currentRoute.value.path
  const parts = path.split('/')
  if (parts.length >= 3 && parts[1] === 'library') {
    const handle = parts[2]
    if (handle !== 'details' && handle !== 'createplaylist') {
      return handle
    }
  }
  return null
}

export const checkIfTooltipHasSpace = (index: number): Placement => {
  const { isMobileLayout } = useViewPort()

  if (isMobileLayout.value) {
    return index % 2 === 1 ? (TOOLTIP_PLACEMENT.leftStart as Placement) : (TOOLTIP_PLACEMENT.rightStart as Placement)
  } else {
    return TOOLTIP_PLACEMENT.rightStart as Placement
  }
}

// checking image file validity used in <input type="file"/> - passing as arg files[0]
export function checkValidUploadingImageType(file: File): boolean {
  const allowedMimeTypes = new Set(['image/jpeg', 'image/pjpeg', 'image/png', 'image/gif', 'image/bmp'])
  return file ? allowedMimeTypes.has(file.type) : false
}

export function formatTags(tags: Array<{ name?: string; id?: number; __CREATE__?: boolean }>): VideoTag[] {
  return tags.map((tag) => {
    const formattedTag: VideoTag = {
      name: tag.name || ''
    }
    if (tag.id && !tag.__CREATE__) {
      formattedTag.id = tag.id
    }
    return formattedTag
  })
}

export async function addFolderVideosToPlaylist(folderId: number, totalVideos: number, organizationId: number, routeParams?: Record<string, string | number>): Promise<void> {
  try {
    const response = await apiService.videos.getVideos({
      folder_id: folderId,
      organization_id: organizationId,
      results_per_page: totalVideos,
      page_number: 1
    })
    const videos = response.videos || []
    if (videos.length > 0) {
      store.commit('playlists/setSelectedVideos', videos)
      const routeConfig: {
        name: string
        query: { new: string }
        params?: Record<string, string | number>
      } = {
        name: 'folder-createplaylist',
        query: { new: 'true' }
      }
      if (routeParams) {
        routeConfig.params = routeParams
      }
      await router.push(routeConfig)
    }
  } catch (err) {
    store.dispatch('messages/addMessage', {
      message: i18n.global.t('error-loading-videos'),
      type: 'fail'
    })
    throw err
  }
}

export function normalizeWebsite(value: string) {
  if (!value) return value
  const trimmed = value.trim()
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed
  }
  return `http://${trimmed}`
}

export function preloadImage(src: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = () => resolve()
    img.src = src
  })
}
